import { useEffect, useState } from "react";
import { fetchOrders, updateOrderStatus } from "../api/orderApi";
import { toast } from "react-toastify";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await fetchOrders();
      setOrders(data);
    } catch (err) {
      console.error("FETCH ORDERS ERROR:", err);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      setUpdatingId(id);

      await updateOrderStatus(id, status);

      toast.success("Order status updated");

      await loadOrders();
    } catch (err) {
      console.error("STATUS UPDATE ERROR:", err);
      toast.error("Status update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusClasses = (status) => {
    switch (status) {
      case "Pending":
        return "bg-yellow-100 text-yellow-700";

      case "Processing":
        return "bg-blue-100 text-blue-700";

      case "Shipped":
        return "bg-purple-100 text-purple-700";

      case "Delivered":
        return "bg-green-100 text-green-700";

      case "Cancelled":
        return "bg-red-100 text-red-700";

      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[250px]">
        <p className="text-sm text-gray-500">
          Loading orders...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-100 p-6 text-center">
        <p className="text-red-600 text-sm">
          {error}
        </p>

        <button
          onClick={loadOrders}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* ================= HEADER ================= */}

      <div className="mb-5 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          Orders
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          Manage and track customer orders
        </p>
      </div>

      {/* ================= DESKTOP TABLE ================= */}

      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-100">
                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Order ID
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  User
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Total
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Status
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Date
                </th>

                <th className="px-5 py-4 text-left font-semibold text-gray-600">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {orders.length > 0 ? (
                orders.map((order) => (
                  <tr
                    key={order._id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition"
                  >
                    {/* Order ID */}

                    <td className="px-5 py-4 font-medium text-gray-800">
                      #{order._id.slice(-6)}
                    </td>

                    {/* User */}

                    <td className="px-5 py-4 text-gray-700">
                      {order.user?.name || "Guest"}
                    </td>

                    {/* Total */}

                    <td className="px-5 py-4 font-medium text-gray-800">
                      ₹
                      {Number(
                        order.totalPrice || 0
                      ).toLocaleString("en-IN")}
                    </td>

                    {/* Status */}

                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusClasses(
                          order.status
                        )}`}
                      >
                        {order.status}
                      </span>
                    </td>

                    {/* Date */}

                    <td className="px-5 py-4 text-gray-600">
                      {new Date(
                        order.createdAt
                      ).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>

                    {/* Action */}

                    <td className="px-5 py-4">
                      <select
                        value={order.status}
                        disabled={updatingId === order._id}
                        onChange={(e) =>
                          handleStatusChange(
                            order._id,
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        <option value="Pending">
                          Pending
                        </option>

                        <option value="Processing">
                          Processing
                        </option>

                        <option value="Shipped">
                          Shipped
                        </option>

                        <option value="Delivered">
                          Delivered
                        </option>

                        <option value="Cancelled">
                          Cancelled
                        </option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-10 text-center text-gray-500"
                  >
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MOBILE CARDS ================= */}

      <div className="md:hidden space-y-3">
        {orders.length > 0 ? (
          orders.map((order) => (
            <div
              key={order._id}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
            >
              {/* Top Row */}

              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-500">
                    Order ID
                  </p>

                  <p className="text-sm font-semibold text-gray-800 mt-1">
                    #{order._id.slice(-6)}
                  </p>
                </div>

                <span
                  className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusClasses(
                    order.status
                  )}`}
                >
                  {order.status}
                </span>
              </div>

              {/* Customer */}

              <div className="mt-4">
                <p className="text-xs text-gray-500">
                  Customer
                </p>

                <p className="text-sm font-medium text-gray-800 mt-1">
                  {order.user?.name || "Guest"}
                </p>
              </div>

              {/* Order Info */}

              <div className="grid grid-cols-2 gap-4 mt-4 pt-3 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500">
                    Total
                  </p>

                  <p className="text-base font-bold text-gray-900 mt-1">
                    ₹
                    {Number(
                      order.totalPrice || 0
                    ).toLocaleString("en-IN")}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">
                    Date
                  </p>

                  <p className="text-sm font-medium text-gray-800 mt-1">
                    {new Date(
                      order.createdAt
                    ).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              {/* Status Update */}

              <div className="mt-4 pt-3 border-t border-gray-100">
                <label className="block text-xs text-gray-500 mb-1.5">
                  Update Status
                </label>

                <select
                  value={order.status}
                  disabled={updatingId === order._id}
                  onChange={(e) =>
                    handleStatusChange(
                      order._id,
                      e.target.value
                    )
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="Pending">
                    Pending
                  </option>

                  <option value="Processing">
                    Processing
                  </option>

                  <option value="Shipped">
                    Shipped
                  </option>

                  <option value="Delivered">
                    Delivered
                  </option>

                  <option value="Cancelled">
                    Cancelled
                  </option>
                </select>

                {updatingId === order._id && (
                  <p className="text-xs text-blue-600 mt-1.5">
                    Updating status...
                  </p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-500">
            No orders found
          </div>
        )}
      </div>
    </div>
  );
}